import {
  ButtonV3,
  type ButtonV3Props,
  type GradientBGVarProps,
  GridItem,
  LayoutGrid,
  RadialGradientBG,
  type RadialGradientBGProps,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ComponentPropsWithoutRef } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { ImageProps } from '@/app/components/Image';
import Image from '@/app/components/Image';
import ArtDirectionImage, {
  type ArtDirectionImageProps,
} from '@/app/components/Image/ArtDirectionImage';
import Motion from '@/app/components/Motion';
import type { SectionHeadingProps } from '@/app/components/SectionHeading';
import SectionHeading from '@/app/components/SectionHeading';

interface LandingPageHeroCta extends Omit<ButtonV3Props, 'children'> {
  label: MessageKey;
}

export interface LandingPageHeroProps
  extends ComponentPropsWithoutRef<'section'> {
  readonly backgroundImage?: ArtDirectionImageProps;
  readonly backgroundProps: {
    color: GradientBGVarProps['background'];
    gradients: RadialGradientBGProps['gradients'];
  };
  readonly copy: MessageKey;
  readonly copyProps?: ComponentPropsWithoutRef<'p'>;
  readonly cta?: LandingPageHeroCta;
  readonly eyebrow?: MessageKey;
  readonly images?: [LandingPageHeroImagesProps, LandingPageHeroImagesProps];
  readonly textColor: string;
  readonly title: MessageKey;
  readonly titleProps?: Pick<
    SectionHeadingProps,
    'indentation' | 'typographyProps'
  >;
}

export interface LandingPageHeroImagesProps extends ImageProps {
  quote?: MessageKey;
  name?: string;
}

const LandingPageHero = ({
  backgroundImage,
  backgroundProps,
  copy,
  copyProps,
  cta,
  eyebrow,
  images,
  textColor,
  title,
  titleProps,
  ...props
}: LandingPageHeroProps): JSX.Element => {
  const t = useTranslations();
  const hasBackgroundImage = Boolean(backgroundImage);

  return (
    <section
      className={cx(
        'relative lg:flex lg:h-auto lg:min-h-svh lg:flex-col lg:px-5 lg:pb-4',
        {
          'h-svh': hasBackgroundImage,
          'min-h-[50vw]': !hasBackgroundImage,
        },
      )}
      data-cy="landing-page-hero"
      {...props}
    >
      <RadialGradientBG
        background={backgroundProps.color}
        className={cx(
          'size-full overflow-hidden lg:flex lg:flex-grow lg:flex-col lg:rounded-b-xl',
          textColor,
        )}
        gradients={backgroundProps.gradients}
      >
        {backgroundImage && (
          <Motion
            animate={{ opacity: 1, scale: 1 }}
            className="absolute left-0 top-0 size-full"
            data-cy="landing-page-hero-bg-image"
            initial={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 1.5, type: 'tween' }}
          >
            <ArtDirectionImage
              {...backgroundImage}
              className="object-cover"
              fill
              priority
            />
          </Motion>
        )}
        <LayoutGrid
          className={cx('relative size-full pb-16 pt-24 lg:flex-grow', {
            'lg:content-between': !hasBackgroundImage,
          })}
          rowGap={0}
        >
          <GridItem
            className={cx({ 'lg:mt-auto lg:self-center': hasBackgroundImage })}
            colEnd={{ sm: 'main', lg: 14, xl: 12 }}
            colStart={{ sm: 'main' }}
            rowEnd={{ sm: 2 }}
            rowStart={{ sm: 1 }}
          >
            <div
              className={cx('my-5 flex flex-col lg:pt-[5vw]', {
                'items-center lg:items-start lg:pb-[8vw]': hasBackgroundImage,
              })}
              data-cy="landing-page-hero-title-wrapper"
            >
              {eyebrow && (
                <Motion
                  initial={{ opacity: 0 }}
                  transition={{ duration: 1, type: 'tween', delay: 0.25 }}
                  viewport={{ once: true }}
                  whileInView={{ opacity: 1 }}
                >
                  <TypographyV3
                    color="inherit"
                    className="uppercase tracking-wide lg:block"
                    data-cy="landing-page-hero-eyebrow"
                    Element="h1"
                    variant="body-large"
                    weight="bold"
                  >
                    {t(eyebrow)}
                  </TypographyV3>
                </Motion>
              )}
              <SectionHeading
                animated
                content={title}
                emAnimationProps={{
                  transition: { duration: 1.5, type: 'tween', delay: 0.5 },
                }}
                indentation={titleProps?.indentation ?? 'pl-28'}
                multiline
                typographyProps={{
                  height: 'tight',
                  ...titleProps?.typographyProps,
                  className: cx(
                    'tracking-tighter lg:max-w-none',
                    titleProps?.typographyProps?.className,
                  ),
                  Element: eyebrow ? 'h2' : 'h1',
                  variant: 'h1',
                  color: 'inherit',
                }}
                wrapperAnimationProps={{
                  transition: { duration: 1, type: 'tween', delay: 0.5 },
                }}
              />
            </div>
          </GridItem>
          <GridItem
            className={cx('lg:self-end', {
              'mb-12 lg:mb-0': !hasBackgroundImage,
            })}
            colEnd={{
              sm: 'main',
              md: hasBackgroundImage ? 22 : 'main',
              lg: 11,
              xl: 9,
            }}
            colStart={{
              sm: 'main',
              md: hasBackgroundImage ? 4 : 'main',
              lg: 'main',
            }}
            data-cy="landing-page-hero-content-wrapper"
            rowEnd={{ sm: 3 }}
            rowStart={{ sm: 2 }}
          >
            <Motion
              className={cx('flex flex-col gap-y-6', {
                'h-full items-center justify-end lg:h-auto lg:items-start lg:justify-start':
                  hasBackgroundImage,
              })}
              data-cy="landing-page-hero-description-wrapper"
              initial={{ opacity: 0 }}
              transition={{ duration: 1, type: 'tween', delay: 0.75 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1 }}
            >
              <TypographyV3
                className={cx(
                  'relative text-base md:text-body-lg',
                  copyProps?.className,
                  {
                    'text-center lg:text-left': hasBackgroundImage,
                  },
                )}
                color="inherit"
                data-cy="landing-page-hero-description"
              >
                {t.rich(copy)}
              </TypographyV3>
              {cta && (
                <ButtonV3 data-cy="landing-page-hero-cta" {...cta}>
                  {t(cta.label)}
                </ButtonV3>
              )}
            </Motion>
          </GridItem>
          {images && (
            <>
              <GridItem
                className="self-end pt-32 md:pt-80 lg:pt-0"
                colEnd={{ sm: 'main', md: 'main', lg: 'main' }}
                colStart={{ sm: 3, md: 5, lg: 16 }}
                rowEnd={{ sm: 4, lg: 3 }}
                rowStart={{ sm: 3, lg: 1 }}
              >
                <Motion
                  className="relative aspect-6/4"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, type: 'tween', delay: 0.75 }}
                >
                  <Image
                    {...images[0]}
                    className="rounded-lg object-cover"
                    data-cy="landing-page-hero-image-0"
                    fill
                    priority
                    sizes="(max-width: 1023px) 80vw, 33vw"
                  />
                  {images[0].quote && (
                    <blockquote className="hidden px-4 pt-[55%] text-sandstone-200 md:relative md:block">
                      <TypographyV3
                        className="relative before:content-[open-quote] after:content-[close-quote]"
                        color="inherit"
                      >
                        {t(images[0].quote)}
                      </TypographyV3>
                      <footer>
                        <TypographyV3 className="relative" color="inherit">
                          {images[0].name}
                        </TypographyV3>
                      </footer>
                    </blockquote>
                  )}
                </Motion>
              </GridItem>
              <GridItem
                className="mb-[45vw] self-end lg:mb-[17vw]"
                colEnd={{ sm: 6, md: 13, lg: 20, xl: 19 }}
                colStart={{ sm: 'main', lg: 14, xl: 14 }}
                rowEnd={{ sm: 4, lg: 3, xl: 3 }}
                rowStart={{ sm: 3, lg: 1, xl: 1 }}
              >
                <Motion
                  className="relative aspect-square"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, type: 'tween', delay: 0.5 }}
                >
                  <Image
                    {...images[1]}
                    className="rounded-lg"
                    data-cy="landing-page-hero-image-1"
                    fill
                    sizes="(max-width: 1023px) 45vw, 25vw"
                    priority
                  />
                  {images[1].quote && (
                    <blockquote className="relative hidden px-4 pt-[80%] text-sandstone-200 md:block">
                      <TypographyV3
                        className="relative before:content-[open-quote] after:content-[close-quote]"
                        color="inherit"
                      >
                        {t(images[1].quote)}
                      </TypographyV3>
                      <footer>
                        <TypographyV3 className="relative" color="inherit">
                          {images[1].name}
                        </TypographyV3>
                      </footer>
                    </blockquote>
                  )}
                </Motion>
              </GridItem>
            </>
          )}
        </LayoutGrid>
      </RadialGradientBG>
    </section>
  );
};

export default LandingPageHero;
